import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './RealtorProfile.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { removeBackground } from '@imgly/background-removal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPen } from '@fortawesome/free-solid-svg-icons';

const CROP_HANDLE_NE = 'crop-handle-ne';
const CROP_HANDLE_SE = 'crop-handle-se';
const CROP_HANDLE_SW = 'crop-handle-sw';
const CROP_HANDLE_NW = 'crop-handle-nw';
const CROP_HANDLE_DIRECTIONS = [CROP_HANDLE_NE, CROP_HANDLE_SE, CROP_HANDLE_SW, CROP_HANDLE_NW];
const MINIMUM_CROP_DIMENSION = 10;
const DIRECTION_TO_CLASSNAME_MAP = {
    [CROP_HANDLE_NE]: 'cropper__handle--ne',
    [CROP_HANDLE_SE]: 'cropper__handle--se',
    [CROP_HANDLE_SW]: 'cropper__handle--sw',
    [CROP_HANDLE_NW]: 'cropper__handle--nw',
};

const getScaledCropMask = ({ x, y, width, height }, scale = 1) => ({
    x: Math.round(x / scale),
    y: Math.round(y / scale),
    width: Math.round(width / scale),
    height: Math.round(height / scale),
});

const pxToStyles = ({ x, y, width, height }) => ({
    left: `${x}px`,
    top: `${y}px`,
    width: `${width}px`,
    height: `${height}px`,
});

const getCropMaskPolygon = (naturalWidth, naturalHeight, cropMask, scale = 1) => {
    let width = Math.round(naturalWidth / scale);
    let height = Math.round(naturalHeight / scale);
    let top = Math.round(cropMask.y / scale);
    let left = Math.round(cropMask.x / scale);
    let bottom = Math.round((cropMask.y + cropMask.height) / scale);
    let right = Math.round((cropMask.x + cropMask.width) / scale);

    return [
        '0,0',
        `${left},0`,
        `${left},${bottom}`,
        `${right},${bottom}`,
        `${right},${top}`,
        `${left},${top}`,
        `${left},0`,
        `${width},0`,
        `${width},${height}`,
        `0,${height}`,
        '0,0',
    ].join(' ');
};

const getDefaultCropMask = (width, height, multiplier = 0.1) => {
    let size = Math.floor(Math.min(width, height) * multiplier);
    let cropSize = Math.floor(Math.min(width, height) - size * 2);
    return {
        x: Math.floor((width - cropSize) / 2),
        y: Math.floor((height - cropSize) / 2),
        width: cropSize,
        height: cropSize,
    };
};

// Utility to timeout long-running promises
const withTimeout = (promise, ms, onTimeout) =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            if (onTimeout) { try { onTimeout(); } catch (e) {} }
            reject(new Error('Operation timed out'));
        }, ms);
        promise
            .then((result) => { clearTimeout(timer); resolve(result); })
            .catch((error) => { clearTimeout(timer); reject(error); });
    });

const getScale = (currentWidth, naturalWidth) => naturalWidth / currentWidth;

// Lightweight, mobile-friendly background removal using MediaPipe SelfieSegmentation
// Returns a PNG Blob with transparent background, or throws on failure
let selfieSegmentationLoaderPromise = null;
const removeBackgroundMobileFriendly = async (inputBlob) => {
    // Lazy-load MediaPipe from CDN if not present; memoize load
    if (!selfieSegmentationLoaderPromise) {
        selfieSegmentationLoaderPromise = new Promise((resolve, reject) => {
            if (window.SelfieSegmentation) return resolve();
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load MediaPipe SelfieSegmentation'));
            document.head.appendChild(script);
        });
    }
    await selfieSegmentationLoaderPromise;

    const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = URL.createObjectURL(inputBlob);
    });

    const width = img.width;
    const height = img.height;
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Support either window.SelfieSegmentation constructor or namespace.SelfieSegmentation
    const SegCtor = typeof window.SelfieSegmentation === 'function'
        ? window.SelfieSegmentation
        : window.SelfieSegmentation && window.SelfieSegmentation.SelfieSegmentation;
    if (!SegCtor) throw new Error('SelfieSegmentation constructor not found');
    const segmentation = new SegCtor({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
    });
    segmentation.setOptions({ modelSelection: 1 }); // Landscape model for better quality

    const segmentationMask = await new Promise((resolve, reject) => {
        segmentation.onResults((results) => {
            try {
                // results.segmentationMask is a canvas
                resolve(results.segmentationMask);
            } catch (e) { reject(e); }
        });
        segmentation.send({ image: img }).catch(reject);
    });

    // Read mask grayscale and apply as alpha onto original image
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = width; maskCanvas.height = height;
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.drawImage(segmentationMask, 0, 0, width, height);
    const maskData = maskCtx.getImageData(0, 0, width, height);
    // Determine if mask needs inversion by sampling border luminance
    const m = maskData.data;
    let sum = 0, count = 0;
    const border = Math.max(2, Math.floor(Math.min(width, height) * 0.02));
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (x < border || y < border || x >= width - border || y >= height - border) {
                const idx = (y * width + x) * 4;
                sum += (m[idx] + m[idx+1] + m[idx+2]) / 3;
                count++;
            }
        }
    }
    const borderMean = count ? (sum / count) : 255;
    const invertMask = borderMean > 128; // if border is white, invert to keep subject

    // Draw original to output canvas and then set alpha from mask
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    const outData = ctx.getImageData(0, 0, width, height);
    const d = outData.data;
    const threshold = 100; // slightly softer threshold
    for (let i = 0; i < d.length; i += 4) {
        const r = m[i], g = m[i+1], b = m[i+2];
        let luminance = (r + g + b) / 3; // 0..255
        if (invertMask) luminance = 255 - luminance;
        d[i+3] = luminance > threshold ? 255 : 0;
    }
    ctx.putImageData(outData, 0, 0);

    return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b || inputBlob), 'image/png', 0.95);
    });
};

// High-quality, still mobile-capable removal using TensorFlow.js BodyPix
// Tries to load tfjs + body-pix from CDN and returns a PNG Blob
let bodyPixLoaderPromise = null;
const removeBackgroundTfjs = async (inputBlob) => {
    if (!bodyPixLoaderPromise) {
        bodyPixLoaderPromise = new Promise((resolve, reject) => {
            const ensureScript = (src) => new Promise((res, rej) => {
                const exists = Array.from(document.scripts).some(s => s.src && s.src.includes(src));
                if (exists) return res();
                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.onload = () => res();
                script.onerror = () => rej(new Error('Failed to load ' + src));
                document.head.appendChild(script);
            });
            Promise.resolve()
                .then(() => ensureScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.13.0/dist/tf.min.js'))
                .then(() => ensureScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/body-pix@2.2.0/dist/body-pix.min.js'))
                .then(resolve)
                .catch(reject);
        });
    }
    await bodyPixLoaderPromise;

    if (!window.bodyPix) throw new Error('BodyPix not available');
    const net = await window.bodyPix.load({ architecture: 'MobileNetV1', outputStride: 16, multiplier: 0.75, quantBytes: 2 });

    const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = URL.createObjectURL(inputBlob);
    });

    const width = img.width;
    const height = img.height;
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');

    const segmentation = await net.segmentPerson(img, { internalResolution: 'medium', segmentationThreshold: 0.7 });
    // segmentation.data is a Uint8Array mask of 0/1
    ctx.drawImage(img, 0, 0, width, height);
    const out = ctx.getImageData(0, 0, width, height);
    const d = out.data;
    const mask = segmentation.data;
    for (let i = 0, p = 0; i < d.length; i += 4, p++) {
        d[i + 3] = mask[p] ? 255 : 0;
    }
    ctx.putImageData(out, 0, 0);

    return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b || inputBlob), 'image/png', 0.95);
    });
};

const getNextMovedCropMask = (dragStartPosition, dragStartCrop, newPosition, scale) => {
    let { pageX: startPageX, pageY: startPageY } = dragStartPosition;
    let { pageX: newPageX, pageY: newPageY } = newPosition;
    let { x, y, width, height } = dragStartCrop;

    return {
        x: Math.floor(x - (startPageX - newPageX) * scale),
        y: Math.floor(y - (startPageY - newPageY) * scale),
        width,
        height,
    };
};

const constrainCropMaskXY = (naturalWidth, naturalHeight, { x, y, width, height }) => {
    if (x + width > naturalWidth) {
        x = naturalWidth - width;
    }
    if (y + height > naturalHeight) {
        y = naturalHeight - height;
    }
    return {
        x: Math.floor(Math.max(0, x)),
        y: Math.floor(Math.max(0, y)),
        width,
        height,
    };
};

const constrainCropMask = (naturalWidth, naturalHeight, { x, y, width, height }) => {
    let size = Math.max(MINIMUM_CROP_DIMENSION, Math.min(width, height, naturalWidth, naturalHeight));
    return {
        ...constrainCropMaskXY(naturalWidth, naturalHeight, { x, y, width: size, height: size }),
        width: Math.floor(size),
        height: Math.floor(size),
    };
};

const centerCropMask = (naturalWidth, naturalHeight, { x, y, width, height }) => {
    let size = Math.min(width, height);
    return {
        x: Math.floor((naturalWidth - size) / 2),
        y: Math.floor((naturalHeight - size) / 2),
        width: size,
        height: size,
    };
};

const getEventCoords = ({ touches, pageX, pageY }) => {
    return touches ? { pageX: touches[0].pageX, pageY: touches[0].pageY } : { pageX, pageY };
};

const HAS_WINDOW = typeof window !== 'undefined';

const addWindowHandler = (eventName, handler) => {
    if (HAS_WINDOW) {
        window.addEventListener(eventName, handler);
    }
};

const removeWindowHandler = (eventName, handler) => {
    if (HAS_WINDOW) {
        window.removeEventListener(eventName, handler);
    }
};

const addMoveHandler = (handler) => {
    addWindowHandler('mousemove', handler);
    addWindowHandler('touchmove', handler);
};

const removeMoveHandler = (handler) => {
    removeWindowHandler('mousemove', handler);
    removeWindowHandler('touchmove', handler);
};

const addActionEndHandler = (handler) => {
    addWindowHandler('mouseup', handler);
    addWindowHandler('touchend', handler);
};

const removeActionEndHandler = (handler) => {
    removeWindowHandler('mouseup', handler);
    removeWindowHandler('touchend', handler);
};

const preloadImage = (url, crossOrigin = 'anonymous') =>
    new Promise((resolve, reject) => {
        let img = new Image();
        img.crossOrigin = crossOrigin;
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.setAttribute('src', url);
    });

const getCroppedImageUrl = (url, cropMask) =>
    preloadImage(url).then((image) => {
        let canvas = document.createElement('canvas');
        canvas.setAttribute('width', 200);
        canvas.setAttribute('height', 200);
        let context = canvas.getContext('2d');
        context.drawImage(
            image,
            cropMask.x,
            cropMask.y,
            cropMask.width,
            cropMask.height,
            0,
            0,
            200,
            200
        );
        return canvas.toDataURL('image/png', 0.9);
    });

const createPlaceholderImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, 200, 200);
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(new File([blob], 'placeholder.png', { type: 'image/png' }));
        }, 'image/png', 0.9);
    });
};

const resizeImage = (blob, maxSize = 512) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            if (width > height && width > maxSize) {
                height = Math.round((height * maxSize) / width);
                width = maxSize;
            } else if (height > maxSize) {
                width = Math.round((width * maxSize) / height);
                height = maxSize;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((resizedBlob) => resolve(resizedBlob), 'image/png', 0.9);
        };
        img.src = URL.createObjectURL(blob);
    });

const Cropper = ({ src, onCrop, crossOrigin = 'anonymous', altText = '' }) => {
    const [naturalWidth, setNaturalWidth] = useState(0);
    const [naturalHeight, setNaturalHeight] = useState(0);
    const [cropMask, setCropMask] = useState(undefined);
    const [scale, setScale] = useState(1);
    const imageRef = useRef(null);
    const finishMoveActionRef = useRef(() => {});
    const finishSizeActionRef = useRef(() => {});

    useEffect(() => {
        const handleWindowSize = () => {
            setScale(getScale(imageRef.current?.clientWidth, naturalWidth));
        };
        addWindowHandler('resize', handleWindowSize);
        return () => removeWindowHandler('resize', handleWindowSize);
    }, [naturalWidth]);

    const handleImageLoaded = () => {
        const { naturalWidth, naturalHeight } = imageRef.current;
        let cropMask = centerCropMask(
            naturalWidth,
            naturalHeight,
            constrainCropMask(naturalWidth, naturalHeight, getDefaultCropMask(naturalWidth, naturalHeight))
        );
        setNaturalWidth(naturalWidth);
        setNaturalHeight(naturalHeight);
        setCropMask(cropMask);
        setScale(getScale(imageRef.current.clientWidth, naturalWidth));
        onCrop(cropMask);
    };

    const handlePortholeMouseDown = (event) => {
        if (event.target.getAttribute('data-handle') || event.target.classList.contains('add-save-btn')) return;
        event.preventDefault();
        event.stopPropagation();

        let dragStartPosition = getEventCoords(event);
        let handleMouseMove = (event) => {
            event.preventDefault();
            event.stopPropagation();
            let unconstrainedNextCropMask = getNextMovedCropMask(
                dragStartPosition,
                cropMask,
                getEventCoords(event),
                scale
            );
            let newCropMask = constrainCropMaskXY(naturalWidth, naturalHeight, unconstrainedNextCropMask);
            setCropMask(newCropMask);
            onCrop(newCropMask);
        };

        let handleMouseUp = (event) => {
            event.preventDefault();
            event.stopPropagation();
            finishMoveActionRef.current();
        };

        finishMoveActionRef.current = () => {
            removeMoveHandler(handleMouseMove);
            removeActionEndHandler(handleMouseUp);
            finishMoveActionRef.current = () => {};
            onCrop(cropMask);
        };

        addMoveHandler(handleMouseMove);
        addActionEndHandler(handleMouseUp);
    };

    const handleHandleMouseDown = (direction, event) => {
        event.preventDefault();
        event.stopPropagation();

        let dragStartPosition = getEventCoords(event);
        let handleMouseMove = (event) => {
            event.preventDefault();
            event.stopPropagation();
            let { pageX: startPageX, pageY: startPageY } = dragStartPosition;
            let { pageX: newPageX, pageY: newPageY } = getEventCoords(event);
            let { x, y, width, height } = cropMask;
            let invertWidth = direction === CROP_HANDLE_SW || direction === CROP_HANDLE_NW;
            let invertHeight = direction === CROP_HANDLE_NW || direction === CROP_HANDLE_NE;
            let sizeAdjustment = Math.max(
                ((startPageX - newPageX) * scale) * (invertWidth ? -1 : 1),
                ((startPageY - newPageY) * scale) * (invertHeight ? -1 : 1)
            );
            let newSize = Math.floor(Math.max(width - sizeAdjustment, height - sizeAdjustment));

            if (newSize + x > naturalWidth) newSize = naturalWidth - x;
            if (newSize + y > naturalHeight) newSize = naturalHeight - y;

            let sizeConstrainedMask = constrainCropMask(naturalWidth, naturalHeight, {
                x,
                y,
                width: newSize,
                height: newSize,
            });

            let xAdjustment = invertWidth ? sizeConstrainedMask.width - width : 0;
            let yAdjustment = invertHeight ? sizeConstrainedMask.height - height : 0;
            let newCropMask = constrainCropMaskXY(naturalWidth, naturalHeight, {
                ...sizeConstrainedMask,
                x: x - xAdjustment,
                y: y - yAdjustment,
            });

            setCropMask(newCropMask);
            onCrop(newCropMask);
        };

        let handleMouseUp = (event) => {
            event.preventDefault();
            event.stopPropagation();
            finishSizeActionRef.current();
        };

        finishSizeActionRef.current = () => {
            removeMoveHandler(handleMouseMove);
            removeActionEndHandler(handleMouseUp);
            finishSizeActionRef.current = () => {};
            onCrop(cropMask);
        };

        addMoveHandler(handleMouseMove);
        addActionEndHandler(handleMouseUp);
    };

    const handleDone = () => {
        if (!cropMask || !cropMask.width || !cropMask.height) {
            toast.error('Please select a valid crop area.');
            console.error('Crop selection failed: Invalid crop dimensions', { cropMask });
            return;
        }
        onCrop(cropMask);
        toast.success('Crop area selected.');
    };

    let svgCropMask = null;
    let porthole = null;

    if (naturalWidth && naturalHeight && cropMask) {
        let scaledCropMask = getScaledCropMask(cropMask, scale);

        svgCropMask = (
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className="cropper__cover">
                <polygon className="cropper__mask" points={getCropMaskPolygon(naturalWidth, naturalHeight, cropMask, scale)} />
                <rect className="cropper__ants--dark" {...scaledCropMask} />
                <rect className="cropper__ants" {...scaledCropMask} />
            </svg>
        );

        porthole = (
            <div
                className="cropper__porthole"
                style={pxToStyles(scaledCropMask)}
                onMouseDown={handlePortholeMouseDown}
                onTouchStart={handlePortholeMouseDown}
            >
                {CROP_HANDLE_DIRECTIONS.map((direction) => (
                    <div
                        key={direction}
                        className={`cropper__handle ${DIRECTION_TO_CLASSNAME_MAP[direction]}`}
                        data-handle="true"
                        onMouseDown={(e) => handleHandleMouseDown(direction, e)}
                        onTouchStart={(e) => handleHandleMouseDown(direction, e)}
                    />
                ))}
                <button
                    className="add-save-btn"
                    onClick={handleDone}
                    data-handle="true"
                    aria-label="Confirm crop area"
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <div className="cropper">
            {svgCropMask}
            {porthole}
            <img
                src={src}
                alt={altText}
                className="cropper__image"
                crossOrigin={crossOrigin}
                ref={imageRef}
                onLoad={handleImageLoaded}
            />
        </div>
    );
};

const RealtorsProfile = () => {
    const navigate = useNavigate();
    const [userId, setUserId] = useState(null);
    const [authToken, setAuthToken] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [showTeamMemberForm, setShowTeamMemberForm] = useState(false);
    const [teamMemberCount, setTeamMemberCount] = useState(1);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageType, setSelectedImageType] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [cropMask, setCropMask] = useState(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [isRemovingBackground, setIsRemovingBackground] = useState(false);
    const [lastTeamMemberPhoto, setLastTeamMemberPhoto] = useState(null);
    const [isImageProcessed, setIsImageProcessed] = useState(false);
    const canvasRef = useRef(null);
    const urlRefs = useRef({});

    useEffect(() => {
        if (showCropModal) {
            document.body.classList.add('no-scroll');
        } else {
            document.body.classList.remove('no-scroll');
        }
        return () => {
            document.body.classList.remove('no-scroll');
        };
    }, [showCropModal]);

    // Warm up background removal model on mount to reduce first-call latency
    useEffect(() => {
        let warmupUrl = null;
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 64; canvas.height = 64;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, 64, 64);
            canvas.toBlob(async (b) => {
                if (!b) return;
                warmupUrl = URL.createObjectURL(b);
                try {
                    await removeBackground(b, { model: 'isnet', outputFormat: 'image/png' });
                } catch (_) {}
            }, 'image/png', 0.8);
        } catch (_) {}
        return () => { if (warmupUrl) URL.revokeObjectURL(warmupUrl); };
    }, []);

    const closePreview = () => {
        // Revoke temporary blob URLs only; keep final saved URLs
        if (selectedImage) {
            if (typeof selectedImage === 'string' && selectedImage.startsWith('blob:')) {
                URL.revokeObjectURL(selectedImage);
            }
            console.log('Revoked selectedImage blob URL:', selectedImage);
        }
        if (selectedImageType && urlRefs.current[selectedImageType]) {
            // Do not revoke if we've already saved the processed image
            if (!isImageProcessed) {
                const savedUrl = urlRefs.current[selectedImageType];
                if (typeof savedUrl === 'string' && savedUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(savedUrl);
                }
                delete urlRefs.current[selectedImageType];
                console.log('Revoked urlRefs blob URL for:', selectedImageType);
            }
        }
        if (croppedImageUrl) {
            if (typeof croppedImageUrl === 'string' && croppedImageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(croppedImageUrl);
                console.log('Revoked croppedImageUrl blob URL:', croppedImageUrl);
            }
        }

        // Reset formData fields only if the image was not processed
        if (!isImageProcessed && selectedImageType) {
            setFormData((prev) => {
                const newFormData = {
                    ...prev,
                    [selectedImageType]: null,
                    [`${selectedImageType}Url`]: null,
                };
                console.log('Reset formData on preview close:', {
                    ...newFormData,
                    profilePhoto: newFormData.profilePhoto ? '[File]' : null,
                    personalLogo: newFormData.personalLogo ? '[File]' : null,
                    brokerageLogo: newFormData.brokerageLogo ? '[File]' : null,
                    profile2: newFormData.profile2 ? '[File]' : null,
                });
                return newFormData;
            });

            // Reset lastTeamMemberPhoto if closing a profile2 image
            if (selectedImageType === 'profile2') {
                setLastTeamMemberPhoto(null);
                console.log('Reset lastTeamMemberPhoto');
            }
        }

        // Reset the file input
        if (selectedImageType) {
            const fileInputs = document.querySelectorAll(`input[name="${selectedImageType}"]`);
            fileInputs.forEach((input) => {
                input.value = '';
                console.log(`Cleared file input for ${selectedImageType}`);
            });
        }

        // Reset all modal-related states
        setShowCropModal(false);
        setSelectedImage(null);
        setCropMask(null);
        setCroppedImageUrl(null);
        setSelectedImageType(null);
        setIsImageProcessed(false);
        console.log('Preview closed, all states reset');
    };

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        website: '',
        profilePhoto: null,
        profilePhotoUrl: null,
        personalLogo: null,
        personalLogoUrl: null,
        brokerageLogo: null,
        brokerageLogoUrl: null,
        profile2: null,
        profile2Url: null,
    });

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const storedUserId = localStorage.getItem('userId');
        if (!token || !storedUserId) {
            navigate('/');
            return;
        }
        setAuthToken(token);
        setUserId(storedUserId);

        return () => {
            Object.values(urlRefs.current).forEach((url) => URL.revokeObjectURL(url));
        };
    }, [navigate]);

    // Helper function to validate and format website URL
    const validateAndFormatWebsiteUrl = (url) => {
        if (!url) return '';
        
        // URL validation regex pattern
        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w.-]*)*\/?$/;
        
        if (!urlRegex.test(url)) {
            return null; // Invalid URL
        }
        
        // Add https:// prefix if not present
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return 'https://' + url;
        }
        
        return url;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            setPhoneNumbers([value]);
        }
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file.');
                console.error('Invalid file type:', file.type);
                e.target.value = '';
                return;
            }
            // Revoke previous blob URL for this image type if it exists
            if (selectedImageType && urlRefs.current[selectedImageType]) {
                URL.revokeObjectURL(urlRefs.current[selectedImageType]);
                delete urlRefs.current[selectedImageType];
                console.log('Revoked previous blob URL for:', selectedImageType);
            }
            const fileUrl = URL.createObjectURL(file);
            setSelectedImage(fileUrl);
            setSelectedImageType(e.target.name);
            setShowCropModal(true);
            setIsImageProcessed(false);
            urlRefs.current[e.target.name] = fileUrl;
            console.log('New blob created:', fileUrl, 'Type:', e.target.name, 'File:', file.name);
        } else {
            toast.error('No file selected.');
            console.error('No file selected');
            e.target.value = '';
        }
    };

    const handleCrop = (cropMask) => {
        setCropMask(cropMask);
        if (cropMask) {
            getCroppedImageUrl(selectedImage, cropMask).then((result) => {
                setCroppedImageUrl(result);
                console.log('Temporary crop preview generated:', result);
            }).catch((error) => {
                console.error('Error generating crop URL:', error);
                toast.error('Error cropping image.');
            });
        } else {
            setCroppedImageUrl(null);
            console.log('Crop mask cleared');
        }
    };

    const handleCropComplete = () => {
        if (!cropMask || !cropMask.width || !cropMask.height) {
            toast.error('Please select a valid crop area.');
            console.error('Crop failed: Invalid crop dimensions', { cropMask });
            return;
        }

        getCroppedImageUrl(selectedImage, cropMask).then((croppedUrl) => {
            fetch(croppedUrl)
                .then((res) => res.blob())
                .then((blob) => {
                    const croppedFile = new File([blob], `${selectedImageType}.png`, { type: 'image/png' });
                    setFormData((prev) => {
                        const newFormData = {
                            ...prev,
                            [selectedImageType]: croppedFile,
                            [`${selectedImageType}Url`]: croppedUrl,
                        };
                        console.log('Updated formData after crop:', {
                            ...newFormData,
                            profilePhoto: newFormData.profilePhoto ? '[File]' : null,
                            personalLogo: newFormData.personalLogo ? '[File]' : null,
                            brokerageLogo: newFormData.brokerageLogo ? '[File]' : null,
                            profile2: newFormData.profile2 ? '[File]' : null,
                        });
                        return newFormData;
                    });
                    if (selectedImageType === 'profile2') {
                        setLastTeamMemberPhoto(croppedFile);
                    }
                    // Update urlRefs with the final cropped URL
                    if (urlRefs.current[selectedImageType]) {
                        URL.revokeObjectURL(urlRefs.current[selectedImageType]);
                    }
                    urlRefs.current[selectedImageType] = croppedUrl;
                    setIsImageProcessed(true);
                    setCroppedImageUrl(null); // Clear temporary preview
                    console.log('Cropped image saved for:', selectedImageType, 'URL:', croppedUrl);
                    toast.success('Cropped image saved.');
                    setShowCropModal(false);
                })
                .catch((error) => {
                    console.error('Error converting cropped image to file:', error);
                    toast.error('Error processing cropped image.');
                });
        }).catch((error) => {
            console.error('Error generating cropped image URL:', error);
            toast.error('Error cropping image.');
        });
    };

    const handleRemoveBackground = async () => {
        if (!croppedImageUrl) {
            toast.error('Please crop the image first.');
            console.error('Remove background failed: No cropped image');
            return;
        }

        setIsRemovingBackground(true);
        try {
            const blob = await fetch(croppedImageUrl).then((res) => res.blob());
            const resizedBlob = await resizeImage(blob, 512);
            const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
            let resultBlob;
            if (!isMobile) {
                // Desktop: try imgly first for best quality
                try {
                    resultBlob = await withTimeout(removeBackground(resizedBlob, {
                        model: 'isnet',
                        outputFormat: 'image/png',
                        alphaMatting: true,
                        alphaMattingForegroundThreshold: 250,
                        alphaMattingBackgroundThreshold: 20,
                        alphaMattingErodeSize: 0,
                    }), 30000);
                } catch (_) {
                    try {
                        resultBlob = await withTimeout(removeBackgroundTfjs(resizedBlob), 20000);
                    } catch (_) {
                        try {
                            resultBlob = await withTimeout(removeBackgroundMobileFriendly(resizedBlob), 15000);
                        } catch (_) {
                            const formData = new FormData();
                            formData.append('image', resizedBlob, `${selectedImageType}.png`);
                            const response = await fetch('https://api.postlii.com/remove-background', { method: 'POST', body: formData });
                            if (!response.ok) throw new Error('Server-side background removal failed');
                            resultBlob = await response.blob();
                        }
                    }
                }
            } else {
                // Mobile path
                try {
                    resultBlob = await withTimeout(removeBackgroundTfjs(resizedBlob), 20000);
                } catch (_) {
                    try {
                        resultBlob = await withTimeout(removeBackgroundMobileFriendly(resizedBlob), 15000);
                    } catch (_) {
                        const formData = new FormData();
                        formData.append('image', resizedBlob, `${selectedImageType}.png`);
                        const response = await fetch('https://api.postlii.com/remove-background', { method: 'POST', body: formData });
                        if (!response.ok) throw new Error('Server-side background removal failed');
                        resultBlob = await response.blob();
                    }
                }
            }
            const resultUrl = URL.createObjectURL(resultBlob);
            const resultFile = new File([resultBlob], `${selectedImageType}.png`, { type: 'image/png' });

            setFormData((prev) => {
                const newFormData = {
                    ...prev,
                    [selectedImageType]: resultFile,
                    [`${selectedImageType}Url`]: resultUrl,
                };
                console.log('Updated formData after background removal:', {
                    ...newFormData,
                    profilePhoto: newFormData.profilePhoto ? '[File]' : null,
                    personalLogo: newFormData.personalLogo ? '[File]' : null,
                    brokerageLogo: newFormData.brokerageLogo ? '[File]' : null,
                    profile2: newFormData.profile2 ? '[File]' : null,
                });
                return newFormData;
            });

            if (selectedImageType === 'profile2') {
                setLastTeamMemberPhoto(resultFile);
            }

            // Update urlRefs with the final background-removed URL
            if (urlRefs.current[selectedImageType]) {
                URL.revokeObjectURL(urlRefs.current[selectedImageType]);
            }
            urlRefs.current[selectedImageType] = resultUrl;
            setIsImageProcessed(true);
            setCroppedImageUrl(null); // Clear temporary preview
            console.log('Background removed for:', selectedImageType, 'URL:', resultUrl);
            toast.success('Background removed successfully.');
            setShowCropModal(false);
        } catch (error) {
            console.error('Error removing background:', error);
            toast.error('Failed to remove background. Using cropped image.');
            if (croppedImageUrl) {
                fetch(croppedImageUrl)
                    .then((res) => res.blob())
                    .then((blob) => {
                        const fallbackFile = new File([blob], `${selectedImageType}.png`, { type: 'image/png' });
                        setFormData((prev) => {
                            const newFormData = {
                                ...prev,
                                [selectedImageType]: fallbackFile,
                                [`${selectedImageType}Url`]: croppedImageUrl,
                            };
                            console.log('Fallback to cropped image:', {
                                ...newFormData,
                                profilePhoto: newFormData.profilePhoto ? '[File]' : null,
                                personalLogo: newFormData.personalLogo ? '[File]' : null,
                                brokerageLogo: newFormData.brokerageLogo ? '[File]' : null,
                                profile2: newFormData.profile2 ? '[File]' : null,
                            });
                            return newFormData;
                        });
                        if (selectedImageType === 'profile2') {
                            setLastTeamMemberPhoto(fallbackFile);
                        }
                        if (urlRefs.current[selectedImageType]) {
                            URL.revokeObjectURL(urlRefs.current[selectedImageType]);
                        }
                        urlRefs.current[selectedImageType] = croppedImageUrl;
                        setIsImageProcessed(true);
                        setCroppedImageUrl(null); // Clear temporary preview
                        console.log('Fallback cropped image used for:', selectedImageType, 'URL:', croppedImageUrl);
                        setShowCropModal(false);
                    })
                    .catch((err) => {
                        console.error('Error using fallback cropped image:', err);
                        toast.error('Error processing fallback image.');
                    });
            } else {
                toast.error('No cropped image available for fallback.');
                console.error('No blob available for fallback');
            }
        } finally {
            setIsRemovingBackground(false);
        }
    };

    const handleAddTeamMember = (e) => {
        e.preventDefault();
        setShowTeamMemberForm(!showTeamMemberForm);
        if (!showTeamMemberForm) {
            setTeamMemberCount((prev) => prev + 1);
        } else {
            setFormData((prev) => ({
                ...prev,
                [`fullName${teamMemberCount}`]: '',
                [`email${teamMemberCount}`]: '',
                [`phone${teamMemberCount}`]: '',
                profile2: null,
                profile2Url: null,
            }));
        }
    };

    const handleTeamMemberInput = () => {
        console.log('Current formData before saving team member:', {
            ...formData,
            profilePhoto: formData.profilePhoto ? '[File]' : null,
            personalLogo: formData.personalLogo ? '[File]' : null,
            brokerageLogo: formData.brokerageLogo ? '[File]' : null,
            profile2: formData.profile2 ? '[File]' : null,
        });

        const newMember = {
            fullName: formData[`fullName${teamMemberCount}`] || '',
            email: formData[`email${teamMemberCount}`] || '',
            phone: formData[`phone${teamMemberCount}`] || '',
            profilePhotoUrl: formData.profile2Url || '',
        };

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!newMember.fullName.trim()) {
            toast.error('Please enter the team member’s full name.');
            console.error('Team member validation failed: Missing full name');
            return;
        }
        if (!newMember.email || !emailRegex.test(newMember.email)) {
            toast.error('Please enter a valid email for the team member.');
            console.error('Team member validation failed: Invalid or missing email', { email: newMember.email });
            return;
        }
        if (!newMember.phone.trim()) {
            toast.error('Please enter a phone number for the team member.');
            console.error('Team member validation failed: Missing phone');
            return;
        }
        if (!formData.profile2 || !newMember.profilePhotoUrl) {
            toast.error('Please upload and crop a profile photo for the team member.');
            console.error('Team member validation failed: Missing profile2 or profilePhotoUrl', {
                profile2: formData.profile2 ? '[File]' : null,
                profilePhotoUrl: newMember.profilePhotoUrl,
            });
            return;
        }

        setTeamMembers([...teamMembers, newMember]);
        setShowTeamMemberForm(false);
        setFormData((prev) => ({
            ...prev,
            [`fullName${teamMemberCount}`]: '',
            [`email${teamMemberCount}`]: '',
            [`phone${teamMemberCount}`]: '',
            profile2: null,
            profile2Url: null,
        }));
        setTeamMemberCount((prev) => prev + 1);
        console.log('Team member added:', newMember);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('FormData before submission:', {
            ...formData,
            profilePhoto: formData.profilePhoto ? `[File: ${formData.profilePhoto.name}, ${formData.profilePhoto.size} bytes]` : null,
            personalLogo: formData.personalLogo ? `[File: ${formData.personalLogo.name}, ${formData.personalLogo.size} bytes]` : null,
            brokerageLogo: formData.brokerageLogo ? `[File: ${formData.brokerageLogo.name}, ${formData.brokerageLogo.size} bytes]` : null,
            profile2: formData.profile2 ? `[File: ${formData.profile2.name}, ${formData.profile2.size} bytes]` : null,
        });
        console.log('TeamMembers before submission:', teamMembers);
        console.log('LastTeamMemberPhoto:', lastTeamMemberPhoto ? `[File: ${lastTeamMemberPhoto.name}, ${lastTeamMemberPhoto.size} bytes]` : null);

        if (!formData.fullName.trim()) {
            toast.error('Please enter your full name.');
            return;
        }
        if (!formData.email.trim()) {
            toast.error('Please enter your email address.');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address.');
            return;
        }
        if (phoneNumbers.length === 0) {
            toast.error('Please add a phone number.');
            return;
        }
        if (formData.website) {
            const formattedUrl = validateAndFormatWebsiteUrl(formData.website);
            if (formattedUrl === null) {
                toast.error('Please enter a valid website URL (e.g., example.com or https://example.com).');
                return;
            }
            setFormData(prev => ({ ...prev, website: formattedUrl }));
        }
        if (!formData.profilePhoto || !formData.profilePhotoUrl || !(formData.profilePhoto instanceof File)) {
            toast.error('Please upload and crop a valid profile photo.');
            console.error('Profile photo validation failed:', { profilePhoto: formData.profilePhoto, profilePhotoUrl: formData.profilePhotoUrl });
            return;
        }
        if (!formData.personalLogo || !formData.personalLogoUrl || !(formData.personalLogo instanceof File)) {
            toast.error('Please upload and crop a valid personal logo.');
            console.error('Personal logo validation failed:', { personalLogo: formData.personalLogo, personalLogoUrl: formData.personalLogoUrl });
            return;
        }
        if (!formData.brokerageLogo || !formData.brokerageLogoUrl || !(formData.brokerageLogo instanceof File)) {
            toast.error('Please upload and crop a valid brokerage logo.');
            console.error('Brokerage logo validation failed:', { brokerageLogo: formData.brokerageLogo, brokerageLogoUrl: formData.brokerageLogoUrl });
            return;
        }
        if (showTeamMemberForm) {
            const newMember = {
                fullName: formData[`fullName${teamMemberCount}`] || '',
                email: formData[`email${teamMemberCount}`] || '',
                phone: formData[`phone${teamMemberCount}`] || '',
                profilePhotoUrl: formData.profile2Url || '',
            };
            if (newMember.fullName || newMember.email || newMember.phone || formData.profile2Url) {
                if (!newMember.fullName || !newMember.email || !newMember.phone || !formData.profile2 || !formData.profile2Url || !(formData.profile2 instanceof File)) {
                    toast.error('Please complete all team member fields, including a valid profile photo, or save the team member first.');
                    console.error('Incomplete team member form:', {
                        fullName: newMember.fullName,
                        email: newMember.email,
                        phone: newMember.phone,
                        profile2: formData.profile2 ? `[File: ${formData.profile2.name}, ${formData.profile2.size} bytes]` : null,
                        profilePhotoUrl: newMember.profilePhotoUrl,
                    });
                    return;
                }
            }
        }
        if (teamMembers.length > 0) {
            for (let i = 0; i < teamMembers.length; i++) {
                const member = teamMembers[i];
                if (!member.fullName || !member.email || !member.phone || !member.profilePhotoUrl) {
                    toast.error(`Please fill out all fields for team member #${i + 1}, including profile photo.`);
                    console.error(`Team member #${i + 1} validation failed:`, member);
                    return;
                }
            }
        }

        const urlParams = new URLSearchParams(window.location.search);
        const currentUserId = urlParams.get('userId');
        if (!currentUserId) {
            toast.error('User ID not found in URL parameters.');
            console.error('Missing userId in URL parameters');
            return;
        }

        const form = new FormData();
        form.append('userId', currentUserId);
        form.append('fullName', formData.fullName);
        form.append('email', formData.email);
        form.append('phoneNumbers', JSON.stringify(phoneNumbers));
        
        const websiteUrl = formData.website || '';
        form.append('website', websiteUrl);
        form.append('teamMembers', JSON.stringify(teamMembers));

        const appendFile = (key, file) => {
            if (file instanceof File && file.size > 0) {
                console.log(`Appending ${key}: [File: ${file.name}, ${file.size} bytes]`);
                form.append(key, file);
            } else {
                console.error(`Invalid file for ${key}:`, file);
                toast.error(`Invalid file for ${key}. Please upload a valid image.`);
            }
        };

        appendFile('profilePhoto', formData.profilePhoto);
        appendFile('personalLogo', formData.personalLogo);
        appendFile('brokerageLogo', formData.brokerageLogo);

        if (teamMembers.length > 0 || (showTeamMemberForm && formData.profile2)) {
            if (formData.profile2) {
                appendFile('profile2', formData.profile2);
            } else if (lastTeamMemberPhoto) {
                appendFile('profile2', lastTeamMemberPhoto);
            } else {
                console.error('profile2 or lastTeamMemberPhoto missing for team members');
                toast.error('Please upload and crop a profile photo for the team member.');
                return;
            }
        }

        for (let pair of form.entries()) {
            console.log(`FormData entry: ${pair[0]} =`, pair[1] instanceof File ? `[File: ${pair[1].name}, ${pair[1].size} bytes]` : pair[1]);
        }

        try {
            const response = await fetch('https://api.postlii.com/api/brokerage/create', {
                method: 'POST',
                body: form,
            });
            if (!response.ok) {
                const errorData = await response.json();
                console.error('API error response:', errorData);
                throw new Error(errorData.message || `Server returned ${response.status}`);
            }
            const data = await response.json();
            console.log('API response:', data);
            if (data.message === 'Profile created successfully!' || data.message === 'Profile updated successfully!') {
                toast.success(data.message);
                setFormData({
                    fullName: '',
                    email: '',
                    phone: '',
                    website: '',
                    profilePhoto: null,
                    profilePhotoUrl: null,
                    personalLogo: null,
                    personalLogoUrl: null,
                    brokerageLogo: null,
                    brokerageLogoUrl: null,
                    profile2: null,
                    profile2Url: null,
                });
                setPhoneNumbers([]);
                setTeamMembers([]);
                setTeamMemberCount(1);
                setShowTeamMemberForm(false);
                setLastTeamMemberPhoto(null);
                setCroppedImageUrl(null);
                setTimeout(() => {
                    window.location.href = `https://postlii.com/Variations/realtors-Variation?token=${authToken}&userId=${currentUserId}`;
                }, 1500);
            }
        } catch (error) {
            console.error('Error creating profile:', error);
            toast.error('Error creating profile: ' + error.message);
        }
    };

    return (
        <div className="add-profile-container">
            <h2>Create Your Realtors Profile</h2>
            <div className="add-form-section">
                <div className="image-upload-section">
                    <div className="image-upload">
                        <div className="upload-placeholder">
                            {formData.profilePhotoUrl ? (
                                <div className="image-preview-container">
                                    <img src={formData.profilePhotoUrl} alt="Profile Preview" className="image-preview" key={formData.profilePhotoUrl} />
                                    <label htmlFor="profilePhoto" className="replace-button">
                                        <FontAwesomeIcon icon={faPen} />
                                    </label>
                                    <input
                                        type="file"
                                        name="profilePhoto"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        id="profilePhoto"
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="file"
                                        name="profilePhoto"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        id="profilePhoto"
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="profilePhoto" className="upload-label">
                                        <span className="camera-icon">📷</span>
                                    </label>
                                </>
                            )}
                        </div>
                        <p>Profile Photo</p>
                        <p className="upload-text">Upload a professional photo</p>
                    </div>
                    <div className="logo-wrapper">
                        <div className="image-upload">
                            <div className="upload-placeholder">
                                {formData.personalLogoUrl ? (
                                    <div className="image-preview-container">
                                        <img src={formData.personalLogoUrl} alt="Personal Logo Preview" className="image-preview" key={formData.personalLogoUrl} />
                                        <label htmlFor="personalLogo" className="replace-button">
                                            <FontAwesomeIcon icon={faPen} />
                                        </label>
                                        <input
                                            type="file"
                                            name="personalLogo"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="personalLogo"
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            name="personalLogo"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="personalLogo"
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="personalLogo" className="upload-label">
                                            <span className="logo-icon">🏢</span>
                                        </label>
                                    </>
                                )}
                            </div>
                            <p>Personal Logo</p>
                            <p className="upload-text">Upload your personal logo</p>
                        </div>
                        <div className="image-upload">
                            <div className="upload-placeholder">
                                {formData.brokerageLogoUrl ? (
                                    <div className="image-preview-container">
                                        <img src={formData.brokerageLogoUrl} alt="Brokerage Logo Preview" className="image-preview" key={formData.brokerageLogoUrl} />
                                        <label htmlFor="brokerageLogo" className="replace-button">
                                            <FontAwesomeIcon icon={faPen} />
                                        </label>
                                        <input
                                            type="file"
                                            name="brokerageLogo"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="brokerageLogo"
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="file"
                                            name="brokerageLogo"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            id="brokerageLogo"
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="brokerageLogo" className="upload-label">
                                            <span className="logo-icon">🏢</span>
                                        </label>
                                    </>
                                )}
                            </div>
                            <p>Brokerage Logo</p>
                            <p className="upload-text">Upload your brokerage logo</p>
                        </div>
                    </div>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="profile-form">
                <div className="form-section">
                    <div className="add-form-section">
                        <h3>PERSONAL INFORMATION</h3>
                        <div className="add-form-group">
                            <label>Full Name</label>
                            <input
                                type="text"
                                name="fullName"
                                placeholder="Enter your full name"
                                value={formData.fullName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="add-form-group">
                            <label>Email Address</label>
                            <div className="input-with-icon">
                                <span className="icon">✉️</span>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="phone-form-group">
                            <label>Phone Number</label>
                            <div className="phone-input-row">
                                <div className="phone-input-with-icon">
                                    <span className="phone-icon">📞</span>
                                    <input
                                        type="number"
                                        name="phone"
                                        placeholder="(123) 456-7890"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="add-form-group">
                            <label>Website</label>
                            <input
                                type="text"
                                name="website"
                                placeholder="Enter your website URL"
                                value={formData.website}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="add-form-section">
                        <h3>TEAM MEMBERS</h3>
                        {showTeamMemberForm && (
                            <div className="team-member-fields">
                                <div className="image-upload">
                                    <div className="upload-placeholder">
                                        {formData.profile2Url ? (
                                            <div className="image-preview-container">
                                                <img src={formData.profile2Url} alt="Profile 2 Preview" className="image-preview" key={formData.profile2Url} />
                                                <label htmlFor="profile2" className="replace-button">
                                                    <FontAwesomeIcon icon={faPen} />
                                                </label>
                                                <input
                                                    type="file"
                                                    name="profile2"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    id="profile2"
                                                    style={{ display: 'none' }}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="file"
                                                    name="profile2"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    id="profile2"
                                                    style={{ display: 'none' }}
                                                />
                                                <label htmlFor="profile2" className="upload-label">
                                                    <span className="camera-icon">📷</span>
                                                </label>
                                            </>
                                        )}
                                    </div>
                                    <p>Team Member Profile</p>
                                    <p className="upload-text">Upload a professional photo</p>
                                </div>
                                <div className="add-form-group">
                                    <label>Team Member Name</label>
                                    <input
                                        type="text"
                                        name={`fullName${teamMemberCount}`}
                                        placeholder="Enter team member's name"
                                        value={formData[`fullName${teamMemberCount}`] || ''}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="add-form-group">
                                    <label>Team Member Email</label>
                                    <div className="input-with-icon">
                                        <span className="icon">✉️</span>
                                        <input
                                            type="email"
                                            name={`email${teamMemberCount}`}
                                            placeholder="teammember@email.com"
                                            value={formData[`email${teamMemberCount}`] || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="phone-form-group">
                                    <label>Team Member Phone</label>
                                    <div className="phone-input-with-icon">
                                        <span className="phone-icon">📞</span>
                                        <input
                                            type="number"
                                            name={`phone${teamMemberCount}`}
                                            placeholder="(123) 456-7890"
                                            value={formData[`phone${teamMemberCount}`] || ''}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <button type="button" className="btn-add-member" onClick={handleTeamMemberInput}>
                                    Save Team Member
                                </button>
                            </div>
                        )}
                        <div className="team-members-list">
                            {teamMembers.map((member, index) => (
                                <div key={index} className="team-member-card">
                                    {member.profilePhotoUrl && (
                                        <img src={member.profilePhotoUrl} alt={member.fullName} className="member-photo" key={member.profilePhotoUrl} />
                                    )}
                                    <div className="member-details">
                                        <h4>{member.fullName}</h4>
                                        <p>{member.email}</p>
                                        <p>{member.phone}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="profile-form-buttonsb">
                        <button
                            type="button"
                            className="add-cancel-btn"
                            onClick={handleAddTeamMember}
                            disabled={isRemovingBackground}
                        >
                            {showTeamMemberForm ? 'Cancel Team Member' : 'Add Team Member'}
                        </button>
                        <button type="submit" className="add-save-btn1" disabled={isRemovingBackground}>
                            Save Profile
                        </button>
                    </div>
                </div>
            </form>
            {showCropModal && (
                <div className="crop-modal">
                    <div className="crop-modal-content">
                        <h3>Crop Image</h3>
                        <button className="preview-close" onClick={closePreview}>
                            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '16px' }} />
                        </button>
                        <div className="crop-image-container">
                            <Cropper src={selectedImage} onCrop={handleCrop} />
                        </div>
                        <div className="crop-buttons">
                            <button
                                className="add-save-btn"
                                onClick={handleCropComplete}
                                disabled={isRemovingBackground}
                                style={{ opacity: isRemovingBackground ? 0.5 : 1, cursor: isRemovingBackground ? 'not-allowed' : 'pointer' }}
                            >
                                Save Crop
                            </button>
                            {(selectedImageType === 'profilePhoto' || selectedImageType === 'profile2') && (
                                <button
                                    className="add-save-btn"
                                    onClick={handleRemoveBackground}
                                    disabled={isRemovingBackground}
                                    style={{ opacity: isRemovingBackground ? 0.5 : 1, cursor: isRemovingBackground ? 'not-allowed' : 'pointer' }}
                                >
                                    {isRemovingBackground ? 'Processing...' : 'Save with Transparent Background'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <ToastContainer closeButton={false} />
        </div>
    );
};

export default RealtorsProfile;